import React, { useState } from 'react';
import { 
  GraduationCap, 
  Globe, 
  BookOpen, 
  TrendingUp, 
  Users, 
  Building, 
  Briefcase, 
  MapPin,
  CheckCircle,
  ArrowRight,
  FileText,
  UserCheck,
  Clock,
  Star,
  Phone,
  Mail,
  Plane,
  Heart
} from 'lucide-react';
import ResponsiveNavbar from './ResponsiveNavbar';

const ServicesPage: React.FC = () => {
  const [activeService, setActiveService] = useState(0);

  const services:any = [
    {
      id: 'student-enrollment',
      icon: <GraduationCap className="w-8 h-8" />,
      title: 'International Student Enrollment',
      subtitle: 'Your Gateway to Canadian Education',
      description: 'Connect with top-notch colleges and universities in Canada for admission and enrollment as international students.',
      image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Application Assistance',
          description: 'We refer your application to reputable educational institutions and provide advisory services from institution selection to application completion.',
          icon: <FileText className="w-6 h-6" />
        },
        {
          title: 'Professional Counseling',
          description: 'Our qualified admission counselors provide personalized advice, craft compelling personal statements, and prepare you for interviews.',
          icon: <UserCheck className="w-6 h-6" />
        },
        {
          title: 'Documentation Support',
          description: 'Expert assistance in gathering, organizing, and verifying necessary documents for college or university applications.',
          icon: <CheckCircle className="w-6 h-6" />
        }
      ],
      process: [
        'Initial consultation and assessment',
        'Institution selection and matching',
        'Application preparation and submission',
        'Interview preparation and coaching',
        'Acceptance and enrollment support'
      ]
    },
    {
      id: 'immigration',
      icon: <Globe className="w-8 h-8" />,
      title: 'Immigration Services',
      subtitle: 'Expert Guidance for Your Canadian Journey',
      description: 'Experienced immigration procedures with professionals who specialize in helping you navigate your journey to Canada.',
      image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Student Visa & Study Permits',
          description: 'Comprehensive support for student visa applications and study permit documentation for your educational journey in Canada.',
          icon: <FileText className="w-6 h-6" />
        },
        {
          title: 'Permanent Residency',
          description: 'Expert guidance on achieving residency status and becoming permanent residents of Canada after completing your studies.',
          icon: <UserCheck className="w-6 h-6" />
        },
        {
          title: 'Service Packages',
          description: 'Choose from Full Representation, Do-It-Yourself, and Peace of Mind packages based on your assistance needs.',
          icon: <CheckCircle className="w-6 h-6" />
        }
      ],
      requirements: [
        'Acceptance letter from Designated Learning Institution (DLI)',
        'Valid passport or travel document',
        'Proof of sufficient funds for tuition and living expenses',
        'Clean criminal record and police certificate',
        'Medical examination if required',
        'Proof of intent to leave Canada after studies'
      ]
    },
    {
      id: 'education-consulting',
      icon: <BookOpen className="w-8 h-8" />,
      title: 'Educational Consulting',
      subtitle: 'Comprehensive Educational Support',
      description: 'Support for students, parents, and educational institutions in navigating the competitive educational landscape.',
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Academic Advising',
          description: 'Guidance on course selection, study strategies, and academic planning to ensure success in educational pursuits.',
          icon: <BookOpen className="w-6 h-6" />
        },
        {
          title: 'College Application Assistance',
          description: 'Support with essay writing, application completion, and interview preparation for college applications.',
          icon: <FileText className="w-6 h-6" />
        },
        {
          title: 'Instructional Design',
          description: 'Work with school authorities to identify challenges and develop student-centered approaches.',
          icon: <Users className="w-6 h-6" />
        }
      ],
      specializations: [
        'Career counselling and exploration',
        'Personalized and blended learning strategies',
        'Organizational strategy development',
        'Leadership strategy consulting',
        'Educational surveys and analytics'
      ]
    },
    {
      id: 'business-planning',
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Business Planning & Engineering',
      subtitle: 'Strategic Management Excellence',
      description: 'Strategic management expertise to help organizations develop company missions, goals, and strategies for achievement.',
      image: 'https://images.unsplash.com/photo-1664575602276-acd073f104c1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Strategic Planning',
          description: 'Develop comprehensive business plans that serve as roadmaps for starting and managing business operations.',
          icon: <TrendingUp className="w-6 h-6" />
        },
        {
          title: 'Process Optimization',
          description: 'Apply engineering principles to optimize business processes, systems, and operations for maximum efficiency.',
          icon: <Users className="w-6 h-6" />
        },
        {
          title: 'Technology Integration',
          description: 'Combine business administration, technology, and IT to improve efficiency and enhance organizational performance.',
          icon: <CheckCircle className="w-6 h-6" />
        }
      ],
      benefits: [
        'Improved operational efficiency',
        'Reduced costs and enhanced performance',
        'Clear business structure and roadmap',
        'Technology-driven solutions',
        'Sustainable growth strategies'
      ]
    },
    {
      id: 'employment',
      icon: <Briefcase className="w-8 h-8" />,
      title: 'Employment Services',
      subtitle: 'Professional Career Support',
      description: 'Connect with professional employment services that support your career goals and professional development.',
      image: 'https://images.unsplash.com/photo-1560472355-536de3962603?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Job Search Assistance',
          description: 'Connect with experienced career coaches and job placement agencies specialized in job search strategies.',
          icon: <Briefcase className="w-6 h-6" />
        },
        {
          title: 'Resume & Cover Letter Writing',
          description: 'Expert resume and cover letter writing services to create compelling documents that highlight your skills.',
          icon: <FileText className="w-6 h-6" />
        },
        {
          title: 'Interview Coaching',
          description: 'Interview preparation with valuable tips, mock interviews, and feedback to present yourself confidently.',
          icon: <UserCheck className="w-6 h-6" />
        }
      ],
      careerServices: [
        'Career assessment and planning',
        'Skills evaluation and development',
        'Job market insights and trends',
        'Professional networking guidance',
        'Career advancement strategies'
      ]
    },
    {
      id: 'travel-tourism',
      icon: <Plane className="w-8 h-8" />,
      title: 'Travel & Tourism',
      subtitle: 'Your Gateway to Global Adventures',
      description: 'Trusted travel planning and tourism services for all your personal and business travel needs with expert guidance.',
      image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Travel Planning',
          description: 'Comprehensive travel planning services including itinerary design, destination research, and trip coordination.',
          icon: <Plane className="w-6 h-6" />
        },
        {
          title: 'Accommodation Services',
          description: 'Hotel and accommodation booking with competitive rates and quality assurance for comfortable stays.',
          icon: <Building className="w-6 h-6" />
        },
        {
          title: 'Transportation Coordination',
          description: 'Complete transportation solutions including flight bookings, airport transfers, and local transportation.',
          icon: <MapPin className="w-6 h-6" />
        }
      ],
      travelServices: [
        'Visa assistance and documentation',
        'Travel insurance coordination',
        'Group travel and corporate bookings',
        'Cultural tours and experiences',
        '24/7 travel support services'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Hero Section */}
      <ResponsiveNavbar/>
      <section className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
            🎯 Comprehensive Solutions
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Our Expert 
            <span className="bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent"> Services</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Tailored solutions designed to meet your unique needs across education, immigration, business, and career development.
          </p>
        </div>
      </section>

      {/* Services Navigation */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {services.map((service:any, index:any) => (
              <button
                key={index}
                onClick={() => setActiveService(index)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all duration-300 text-sm lg:text-base ${
                  activeService === index
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 shadow-sm'
                }`}
              >
                {service.icon}
                <span className="hidden sm:inline">{service.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Active Service Detail */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
            {/* Service Header with Image */}
            <div className="relative">
              <div className="h-64 sm:h-80 lg:h-96 overflow-hidden">
                <img 
                  src={services[activeService].image} 
                  alt={services[activeService].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-indigo-700/90"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white p-8">
                  <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    {services[activeService].icon}
                  </div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">{services[activeService].title}</h2>
                  <p className="text-blue-100 text-lg sm:text-xl mb-4">{services[activeService].subtitle}</p>
                  <p className="text-blue-50 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
                    {services[activeService].description}
                  </p>
                </div>
              </div>
            </div>

            {/* Service Features */}
            <div className="p-8 sm:p-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Key Features</h3>
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {services[activeService].features.map((feature:any, index:any) => (
                  <div key={index} className="group">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>

              {/* Additional Service Info */}
              <div className="grid md:grid-cols-2 gap-8">
                {services[activeService].process && (
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-blue-600" />
                      Our Process
                    </h4>
                    <ul className="space-y-3">
                      {services[activeService].process.map((step:any, index:any) => (
                        <li key={index} className="flex items-start">
                          <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">
                            {index + 1}
                          </div>
                          <span className="text-gray-700">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {services[activeService].requirements && (
                  <div className="bg-blue-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
                      Requirements
                    </h4>
                    <ul className="space-y-2">
                      {services[activeService].requirements.map((req:any, index:any) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {services[activeService].specializations && (
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Star className="w-5 h-5 mr-2 text-blue-600" />
                      Specializations
                    </h4>
                    <ul className="space-y-2">
                      {services[activeService].specializations.map((spec:any, index:any) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <Star className="w-4 h-4 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                          {spec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {services[activeService].benefits && (
                  <div className="bg-blue-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                      Benefits
                    </h4>
                    <ul className="space-y-2">
                      {services[activeService].benefits.map((benefit:any, index:any) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {services[activeService].careerServices && (
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Briefcase className="w-5 h-5 mr-2 text-blue-600" />
                      Career Services
                    </h4>
                    <ul className="space-y-2">
                      {services[activeService].careerServices.map((service:any, index:any) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {services[activeService].travelServices && (
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Plane className="w-5 h-5 mr-2 text-blue-600" />
                      Travel Services
                    </h4>
                    <ul className="space-y-2">
                      {services[activeService].travelServices.map((service:any, index:any) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Services Overview */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">All Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive solutions for every aspect of your educational and professional journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <GraduationCap className="w-8 h-8" />,
                title: 'International Student Enrollment',
                description: 'Canadian college and university admissions support',
                features: ['Application Assistance', 'Counseling', 'Documentation'],
                image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <Globe className="w-8 h-8" />,
                title: 'Immigration Services',
                description: 'Complete immigration support for Canada',
                features: ['Student Visas', 'Study Permits', 'Permanent Residency'],
                image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <BookOpen className="w-8 h-8" />,
                title: 'Educational Consulting',
                description: 'Comprehensive educational guidance and support',
                features: ['Academic Advising', 'College Applications', 'Career Counselling'],
                image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <TrendingUp className="w-8 h-8" />,
                title: 'Business Planning & Engineering',
                description: 'Strategic management and business optimization',
                features: ['Strategic Planning', 'Process Optimization', 'Technology Integration'],
                image: 'https://images.unsplash.com/photo-1664575602276-acd073f104c1?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: 'Change Management',
                description: 'Guide organizations through transitions effectively',
                features: ['Strategy Development', 'Implementation', 'Training'],
                image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <Building className="w-8 h-8" />,
                title: 'Business & Brand Registration',
                description: 'Legal business setup and brand protection',
                features: ['Business Registration', 'Brand Registration', 'Compliance'],
                image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <Briefcase className="w-8 h-8" />,
                title: 'Employment Services',
                description: 'Professional career development support',
                features: ['Job Search', 'Resume Writing', 'Interview Coaching'],
                image: 'https://images.unsplash.com/photo-1560472355-536de3962603?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <Plane className="w-8 h-8" />,
                title: 'Travel & Tourism',
                description: 'Complete travel planning and tourism services',
                features: ['Travel Planning', 'Accommodations', 'Transportation'],
                image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <UserCheck className="w-8 h-8" />,
                title: 'Career Counselling',
                description: 'Personalized career guidance and development',
                features: ['Career Assessment', 'Job Strategies', 'Development Planning'],
                image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 group overflow-hidden">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{service.description}</p>
                  <ul className="space-y-1">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-xs text-gray-500">
                        <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Let our expert team help you achieve your goals. Schedule a consultation today to discuss your specific needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href='/consultations' className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg flex items-center justify-center">
              <Phone className="w-5 h-5 mr-2" />
              Schedule Consultation
            </a>
            <a href='/contact-us' className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 flex items-center justify-center">
              <Mail className="w-5 h-5 mr-2" />
              Send Inquiry
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">S</span>
                </div>
                <span className="text-xl font-bold">SmeHub</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Your trusted partner in educational consulting, immigration services, and comprehensive business solutions.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Student Enrollment</li>
                <li>Immigration Services</li>
                <li>Educational Consulting</li>
                <li>Business Planning</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact Nigeria</h4>
              <div className="space-y-3 text-gray-400">
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">2nd floor, Oludaisi Ogundare House, 19/21 Balogun street, Anifowose, Ikeja, Lagos</span>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+234 812 2654683</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 mr-2" />
                  <span>enquiry@smehub.ng</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact Canada</h4>
              <div className="space-y-3 text-gray-400">
              <div className="flex items-start">
                <MapPin className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm">4516 Valiant Drive, NW, Calgary, T3A 0Y1, Alberta</span>
              </div>
              <div className="flex items-center">
                <Phone className="w-5 h-5 mr-2" />
                <span>+1 825 935 7540</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
          <p>&copy; 2025 SmeHub Consulting Limited. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
);
};

export default ServicesPage;